<template>
  <div class="order-recap">
    <!-- testata blu: logo del prodotto acquistato e numero d'ordine -->
    <div class="order-recap__header">
      <img
        v-if="order.product.logo_white"
        class="order-recap__logo"
        :src="order.product.logo_white"
        alt=""
      />

      <div class="order-recap__number">
        <p class="order-recap__number-label">Numero Ordine</p>
        <p class="order-recap__number-value">{{ order.number }}</p>
      </div>
    </div>

    <div class="order-recap__body">
      <h2 class="order-recap__title">{{ order.product.name }}</h2>
      <dl class="order-recap__list">
        <div class="order-recap__row">
          <dt class="order-recap__term">Valore</dt>
          <dd class="order-recap__value">€{{ order.amount }}</dd>
        </div>
        <div class="order-recap__row">
          <dt class="order-recap__term">Validità</dt>
          <dd class="order-recap__value">{{ order.validity }}</dd>
        </div>
        <div class="order-recap__row">
          <dt class="order-recap__term">Stato</dt>
          <dd class="order-recap__value order-recap__value--active">{{ order.status }}</dd>
        </div>
      </dl>

      <div class="order-recap__code">
        <div class="order-recap__code-head">
          <div>
            <p class="order-recap__code-label">Il tuo codice Gift Card</p>
            <p class="order-recap__code-value">{{ order.code }}</p>
          </div>

          <button
            type="button"
            class="order-recap__copy"
            @click="copy_code()"
          >
            {{ copied ? 'Copiato' : 'Copia' }}
          </button>
        </div>

        <p class="order-recap__code-note">
          Conserva questo codice per utilizzare la tua Gift Card durante la prenotazione
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';

  // quanto resta scritto "Copiato" sul pulsante, in millisecondi
  const COPIED_RESET_DELAY = 2500;

  export default {
    name: 'order-recap',

    data() {
      return {
        // esito della copia negli appunti: cambia la sola label del pulsante
        copied: false,
        // handle del timer che riporta la label a "Copia"
        copied_timer: null,
      };
    },

    computed: {
      // l'ordine emesso è l'unica fonte della pagina: taglio e marchio li porta
      // build_order dalla response, non la selezione fatta in home — che a
      // ordine concluso è già stata azzerata (vedi reset_purchase nello store)
      ...mapState('livegift', ['order']),
    },

    beforeDestroy() {
      clearTimeout(this.copied_timer);
    },

    methods: {
      copy_code() {
        if (!navigator.clipboard) return;

        navigator.clipboard.writeText(this.order.code).then(
          () => {
            this.copied = true;
            clearTimeout(this.copied_timer);
            this.copied_timer = setTimeout(() => {
              this.copied = false;
            }, COPIED_RESET_DELAY);
          },
          // la copia si può negare (permessi, contesto non sicuro): il ramo va
          // dichiarato o la promise rifiutata resta non gestita. Il codice è
          // comunque a video e selezionabile a mano, la label non mente
          (error) => {
            this.log('[order-recap] - clipboard non disponibile');
            this.log(error);
          },
        );
      },
    },
  };
</script>

<style lang="scss" scoped>

  .order-recap {
    overflow: hidden; // il raggio della card taglia la testata blu
    background-color: $white;
    border-radius: $border-radius-lg * 4;
    box-shadow: 0 10px 15px -3px rgba($black, .05);

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: $grid-gutter-20 $grid-gutter-width;
      background-image: linear-gradient(90deg, $primary 0%, $livegift-blue-dark 100%);
    }

    &__logo {
      display: block;
      flex-shrink: 0;
      width: 130px;
    }

    &__number {
      text-align: right;
    }

    &__number-label {
      margin: 0;
      font-size: $font-size-md;
      color: rgba($white, .9);
    }

    &__number-value {
      margin: 0;
      font-size: $font-size-xl;
      font-weight: $vb-font-weight-semibold;
      color: $white;
    }

    &__body {
      padding: $grid-gutter-width;
    }

    &__title {
      margin-bottom: 20px;
      font-size: $font-size-lg;
      font-weight: $vb-font-weight-semibold;
      color: $gray-900;
    }

    &__list {
      margin: ($grid-gutter-width / 3) 0 0;
    }

    &__row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;

      & + & {
        margin-top: $grid-gutter-width / 3;
      }
    }

    &__term {
      margin: 0;
      font-weight: $font-weight-normal;
      color: $gray-800;
    }

    &__value {
      margin: 0;
      font-weight: $vb-font-weight-semibold;
      color: $gray-900;
      text-align: right;
      word-break: break-word;

      &--active {
        color: $success;
      }
    }

    // riquadro azzurro col codice della card
    &__code {
      padding: $grid-gutter-20;
      margin-top: $grid-gutter-width;
      background-color: #eef2ff;
      border: 2px solid rgba($primary, .3);
      border-radius: $border-radius-lg * 2;
    }

    &__code-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }

    &__code-label {
      margin: 0;
      font-size: $font-size-md;
      color: $gray-700;
    }

    &__code-value {
      margin: 4px 0 0;
      font-size: $font-size-base * 1.5;
      font-weight: $vb-font-weight-semibold;
      letter-spacing: 1.2px;
      color: $primary;
    }

    &__copy {
      flex-shrink: 0;
      padding: 0;
      font-size: $font-size-md;
      color: $info;
      background: none;
      border: 0;

      // in stampa il codice si legge, non si copia
      @media print {
        display: none;
      }
    }

    &__code-note {
      margin: $grid-gutter-20 0 0;
      font-size: $font-size-sm;
      color: $gray-700;
    }

    // sul foglio l'ombra non viene resa: il bordo tiene i confini della card
    @media print {
      border: $border-width solid $gray-200;
      box-shadow: none;
    }
  }
</style>
